// Inline-editable field. Renders read-mode (value + pencil on hover) →
// click pencil → edit mode (input + ✓ ✗). Save on Enter for single-line.
// Save calls onSave(newValue) which is expected to be async (returns Promise);
// the field shows a saving indicator until the promise resolves.
//
// Variants: text (single line), textarea (multi line), select (dropdown),
// date (date input).

import { useState, useRef, useEffect } from 'react';

export const EditableField = ({
  value,
  onSave,
  variant = 'text',           // 'text' | 'textarea' | 'select' | 'date'
  options,                    // for select: [{value, label}]
  placeholder = '点击编辑',
  className = '',
  displayClassName = '',
  ariaLabel,
  disabled = false
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  function start() { if (!disabled && !editing) setEditing(true); }
  function cancel() { setDraft(value ?? ''); setEditing(false); }
  async function save() {
    if ((draft ?? '') === (value ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft === '' ? null : draft);
      setEditing(false);
    } catch (e) {
      // Caller's toast handles the error; restore draft
    } finally {
      setSaving(false);
    }
  }

  function onKey(e) {
    if (e.key === 'Enter' && variant !== 'textarea' && !e.shiftKey) {
      e.preventDefault();
      save();
    } else if (e.key === 'Enter' && variant === 'textarea' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  }

  if (!editing) {
    const isEmpty = value === null || value === undefined || value === '';
    const display = (() => {
      if (isEmpty) return placeholder;
      if (variant === 'select' && options) {
        return options.find(o => o.value === value)?.label || value;
      }
      return value;
    })();
    return (
      <span className={`editable-field ${disabled ? 'disabled' : ''} ${className}`}
            role="button" tabIndex={disabled ? -1 : 0}
            onClick={start}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); start(); } }}
            aria-label={ariaLabel}>
        <span className={`editable-display ${isEmpty ? 'placeholder' : ''} ${displayClassName}`}>
          {display}
        </span>
        {!disabled && <span className="editable-pencil" aria-hidden="true">✎</span>}
      </span>
    );
  }

  // Edit mode
  return (
    <span className={`editable-field editing ${className}`}>
      {variant === 'textarea' ? (
        <textarea ref={inputRef} className="vava-textarea editable-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  rows={5} />
      ) : variant === 'select' ? (
        <select ref={inputRef} className="vava-select editable-input"
                value={draft ?? ''}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}>
          <option value="">—</option>
          {(options || []).map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input ref={inputRef}
               type={variant === 'date' ? 'date' : 'text'}
               className="vava-input editable-input"
               value={draft ?? ''}
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={onKey} />
      )}
      <button className="editable-btn save" onClick={save} disabled={saving} aria-label="保存">
        {saving ? '…' : '✓'}
      </button>
      <button className="editable-btn cancel" onClick={cancel} disabled={saving} aria-label="取消">✗</button>
    </span>
  );
};
